(() => {
  'use strict';
  if (customElements.get('vx-catalog')) return;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.templates = [...this.querySelectorAll('[data-gallery-template]')];
      if (!this.templates.length || this.ready) return;
      this.ready = true;
      this.slides = [this.querySelector('[data-slide]')];
      this.index = 0;
      this.visible = false;
      this.hovered = false;
      this.explicit = false;
      this.paused = false;
      this.loading = false;
      this.initialAdvancePending = true;
      this.initialRemaining = 1800;
      this.initialTimerStartedAt = null;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.toggle = this.querySelector('[data-gallery-toggle]');
      this.counter = this.querySelector('[data-gallery-count]');
      this.next = this.querySelector('[data-gallery-next]');
      this.note = this.querySelector('.vxc-detail-note');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      try { this.paused = sessionStorage.getItem(`vxc-paused-${this.dataset.product}`) === 'true'; } catch (_) { /* Storage is optional. */ }
      [this.toggle, this.counter, this.next].forEach(el => { el.hidden = false; });
      this.addEventListener('pointerenter', () => { this.hovered = this.pointer.matches; this.sync(); }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, options);
      this.addEventListener('focusin', () => { this.paused = true; this.explicit = false; this.sync(); }, options);
      this.toggle.addEventListener('pointerdown', () => { this.wasRunning = this.hasAttribute('data-playing'); }, options);
      this.toggle.addEventListener('click', event => {
        const stop = event.detail > 0 ? this.wasRunning : this.hasAttribute('data-playing');
        this.paused = Boolean(stop);
        this.explicit = !stop;
        try { sessionStorage.setItem(`vxc-paused-${this.dataset.product}`, String(this.paused)); } catch (_) { /* Storage is optional. */ }
        this.sync();
      }, options);
      this.next.addEventListener('click', () => { this.paused = true; this.explicit = false; this.sync(); this.advance(true); }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => { this.explicit = false; this.sync(); }, options);
      this.pointer.addEventListener('change', () => { this.hovered = false; this.sync(); }, options);
      navigator.connection?.addEventListener('change', () => this.sync(), options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].intersectionRatio >= .35;
        this.sync();
      }, { threshold: [0, .35] });
      this.observer.observe(this);
      this.sync();
    }

    canRun() {
      return this.visible && !document.hidden && !this.paused &&
        (this.explicit || (!this.motion.matches && !navigator.connection?.saveData &&
          (this.hasAttribute('data-autoplay') || this.hovered)));
    }

    clearTimer() {
      clearTimeout(this.timer);
      if (this.initialTimerStartedAt != null) {
        this.initialRemaining = Math.max(0, this.initialRemaining - Math.max(0, Date.now() - this.initialTimerStartedAt));
        this.initialTimerStartedAt = null;
      }
    }

    sync() {
      this.clearTimer();
      if (!this.toggle) return;
      const running = this.canRun();
      this.toggleAttribute('data-playing', running);
      this.toggle.setAttribute('aria-label', running ? this.dataset.pauseLabel : this.dataset.playLabel);
      if (running && !this.loading) {
        const initial = this.initialAdvancePending && this.hasAttribute('data-autoplay') && !this.explicit && this.index === 0;
        const delay = initial ? this.initialRemaining : this.hasAttribute('data-autoplay') ? 6000 : 3800;
        if (initial) this.initialTimerStartedAt = Date.now();
        this.timer = setTimeout(() => this.advance(), delay);
      }
    }

    async advance(manual = false) {
      if (this.loading || !this.isConnected || (!manual && !this.canRun())) return;
      this.loading = true;
      this.clearTimer();
      this.initialAdvancePending = false;
      this.initialRemaining = 0;
      const nextIndex = (this.index + 1) % (this.templates.length + 1);
      try {
        let nextSlide = this.slides[nextIndex];
        if (!nextSlide) {
          nextSlide = this.templates[nextIndex - 1].content.firstElementChild.cloneNode(true);
          const img = nextSlide.querySelector('img');
          img.loading = 'eager';
          this.querySelector('.vxc-photo-link').append(nextSlide);
          this.slides[nextIndex] = nextSlide;
          await img.decode();
        }
        if (!this.isConnected || (!manual && !this.canRun())) return;
        this.slides[this.index].classList.remove('is-active');
        this.slides[this.index].setAttribute('aria-hidden', 'true');
        nextSlide.classList.add('is-active');
        nextSlide.removeAttribute('aria-hidden');
        this.index = nextIndex;
        this.counter.textContent = `${nextIndex + 1} / ${this.templates.length + 1}`;
        if (this.note) this.note.hidden = !nextSlide.hasAttribute('data-vxc-detail-note');
      } catch (_) {
        this.paused = true;
        this.explicit = false;
        const failed = this.slides[nextIndex];
        if (failed) failed.remove();
        this.slides[nextIndex] = undefined;
      } finally {
        this.loading = false;
        if (this.isConnected) this.sync();
      }
    }

    disconnectedCallback() {
      this.clearTimer();
      this.observer?.disconnect();
      this.abort?.abort();
      this.ready = false;
    }
  }

  class Catalog extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.form = this.querySelector('[data-filter-form]');
      this.fallback = this.querySelector('.vxc-filter-fallback');
      this.dialog = document.createElement('dialog');
      if (!this.dialog.showModal) return;
      this.dialog.className = 'vxc-dialog';
      this.dialog.setAttribute('aria-labelledby', `VxcFilterTitle-${this.dataset.section}`);
      this.append(this.dialog);
      this.dialog.append(this.form);
      this.fallback.open = false;
      this.querySelector('.vxc-quick').hidden = false;
      this.querySelector('[data-close-filter]').hidden = false;
      this.classList.add('is-enhanced');
      this.prefetchTimers = new Map();
      this.preservePreview();
      this.gridViewport = matchMedia('(min-width: 990px)');
      this.positionEditorials();
      this.gridViewport.addEventListener('change', () => this.positionEditorials(), options);
      this.addEventListener('click', event => {
        const catalogNav = event.target.closest?.('[data-catalog-nav]');
        if (catalogNav) {
          if (Catalog.isPlainNavigation(event, catalogNav)) {
            const targetURL = this.prepareNavigationURL(catalogNav.href);
            if (targetURL) {
              event.preventDefault();
              this.load(targetURL, {
                focus: 'count',
                scrollTop: true,
                routeHandle: catalogNav.dataset.catalogRouteHandle,
                profile: catalogNav.dataset.catalogProfile,
              });
            }
          }
          return;
        }
        const colorOption = event.target.closest('[data-vxc-color-option]');
        if (colorOption && Catalog.isPlainActivation(event)) {
          this.handleColorActivation(event, colorOption);
          return;
        }
        const opener = event.target.closest('[data-open-filter]');
        if (opener) { event.preventDefault(); this.openFilters(opener); return; }
        if (event.target.closest('[data-close-filter]')) { this.dialog.close(); return; }
        if (event.target.closest('[data-all-groups]')) { this.showGroups('all'); return; }
        const link = event.target.closest('[data-results-link]');
        if (link && Catalog.isPlainNavigation(event, link)) {
          const targetURL = this.prepareNavigationURL(link.href);
          if (!targetURL) return;
          event.preventDefault();
          this.load(targetURL, { focus: 'count', scroll: link.closest('.vxc-pagination') !== null });
        }
      }, options);
      this.addEventListener('pointerover', event => {
        const colorOption = event.target.closest?.('[data-vxc-color-option]');
        if (!colorOption || event.pointerType !== 'mouse' || event.relatedTarget?.closest?.('[data-vxc-color-option]') === colorOption) return;
        this.selectColor(colorOption);
      }, options);
      this.addEventListener('pointerover', event => {
        const link = event.target.closest?.('[data-catalog-nav]');
        if (link && event.pointerType !== 'touch') this.schedulePrefetch(link);
      }, options);
      this.addEventListener('pointerout', event => {
        const link = event.target.closest?.('[data-catalog-nav]');
        if (!link || event.relatedTarget?.closest?.('[data-catalog-nav]') === link) return;
        this.cancelPrefetchIntent(link);
      }, options);
      this.addEventListener('focusin', event => {
        const link = event.target.closest?.('[data-catalog-nav]');
        if (link) this.schedulePrefetch(link);
      }, options);
      this.addEventListener('focusout', event => {
        const link = event.target.closest?.('[data-catalog-nav]');
        if (!link || event.relatedTarget?.closest?.('[data-catalog-nav]') === link) return;
        this.cancelPrefetchIntent(link);
      }, options);
      this.addEventListener('keydown', event => {
        const colorOption = event.target.closest?.('[data-vxc-color-option]');
        if (!colorOption || !Catalog.isPlainKeyboardActivation(event)) return;
        this.handleColorActivation(event, colorOption);
      }, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.load(this.formURL(), { focus: this.lastFilter || 'all' });
      }, options);
      this.querySelector('[data-sort]').addEventListener('change', () => this.load(this.formURL(), { focus: 'sort' }), options);
      this.dialog.addEventListener('close', () => {
        this.form.reset();
        this.opener?.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, options);
      window.addEventListener('popstate', event => this.load(new URL(location.href), {
        history: false,
        focus: 'count',
        restoreScroll: event.state?.vxcCatalogScrollY,
      }), options);
    }

    preservePreview() {
      const current = new URL(location.href);
      this.querySelectorAll('a[href]').forEach(link => {
        const rawHref = link.getAttribute?.('href') || link.href || '';
        if (!rawHref || rawHref.includes('#') || link.hasAttribute?.('download')) return;
        const target = this.prepareNavigationURL(rawHref, current, { catalogView: false });
        if (!target || (target.hash && target.pathname === current.pathname && target.search === current.search)) return;
        if (target) link.href = target.href;
      });
      for (const key of ['preview_theme_id', '_fd', 'pb']) {
        const value = key === 'preview_theme_id' ? this.previewThemeId(current) : current.searchParams.get(key);
        if (!value) continue;
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = key; input.value = value; this.form.append(input);
      }
    }

    prefetchTargetForLink(link) {
      if (!link?.hasAttribute?.('data-catalog-nav') || link.hasAttribute?.('download')) return null;
      const rawHref = link.getAttribute?.('href') || link.href || '';
      if (!rawHref || rawHref.includes('#')) return null;
      const target = this.prepareNavigationURL(rawHref);
      if (!target || target.origin !== location.origin) return null;
      const handle = Catalog.collectionHandle(target);
      const profile = Catalog.routeProfile(handle);
      if (!handle || !profile) return null;
      if (link.dataset?.catalogRouteHandle && link.dataset.catalogRouteHandle !== handle) return null;
      if (link.dataset?.catalogProfile && link.dataset.catalogProfile !== profile) return null;
      return target;
    }

    schedulePrefetch(link) {
      const target = this.prefetchTargetForLink(link);
      if (!target) return;
      this.cancelPrefetchIntent(link);
      const timer = setTimeout(() => {
        this.prefetchTimers?.delete(link);
        Catalog.enqueuePrefetch(this, target, {
          routeHandle: link.dataset.catalogRouteHandle,
          profile: link.dataset.catalogProfile,
        });
      }, Catalog.prefetchDelay());
      this.prefetchTimers?.set(link, timer);
    }

    cancelPrefetchIntent(link) {
      const timer = this.prefetchTimers?.get(link);
      if (timer) clearTimeout(timer);
      this.prefetchTimers?.delete(link);
    }

    static routeProfile(handle) {
      return {
        sonnenbrillen: 'sun',
        'damen-sonnenbrillen': 'sun',
        'herren-sonnenbrillen': 'sun',
        brillen: 'optical',
        'einstaerkenbrillen-damen': 'optical',
        'einstaerkenbrillen-herren': 'optical',
        gleitsichtbrillen: 'progressive',
        'brillenfassungen-ohne-fruehlingsaktion': 'no_rx',
      }[handle] || '';
    }

    static routeKey(url) {
      return `${url.origin}${url.pathname.replace(/\/+$/, '') || '/'}`;
    }

    static localePrefix(pathname) {
      const match = pathname.match(/^(.*)\/collections\//i);
      return match ? match[1].replace(/\/+$/, '') : '';
    }

    static collectionHandle(url) {
      const match = url.pathname.match(/\/collections\/([^/]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    }

    static viewForURL(url, catalog) {
      return url.searchParams.get('view') || catalog.dataset.view || 'catalog';
    }

    static pageForURL(url) {
      return url.searchParams.get('page') || '1';
    }

    static normalizeSingletonParams(url) {
      for (const key of ['view', 'preview_theme_id', 'pb', 'theme', '_fd']) {
        const values = url.searchParams.getAll(key);
        if (values.length) url.searchParams.set(key, values[0]);
        else url.searchParams.delete(key);
      }
      return url;
    }

    static hasRepeatedFilterValues(url) {
      const counts = new Map();
      for (const key of url.searchParams.keys()) {
        if (!key.startsWith('filter.')) continue;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      return [...counts.values()].some(count => count > 1);
    }

    static collapseFilterValues(url) {
      const collapsed = new URL(url.href);
      const keys = [...new Set([...collapsed.searchParams.keys()].filter(key => key.startsWith('filter.')))];
      keys.forEach(key => {
        const values = collapsed.searchParams.getAll(key);
        if (values.length) collapsed.searchParams.set(key, values[0]);
      });
      return collapsed;
    }

    static prefetchDelay() {
      return 120;
    }

    static prefetchQueueLimit() {
      return 6;
    }

    static requestTimeout() {
      return 15000;
    }

    static prefetchAllowed() {
      const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
      return !connection?.saveData && connection?.effectiveType !== 'slow-2g';
    }

    static prefetchStore() {
      if (!Catalog._prefetchStore) {
        Catalog._prefetchStore = { cache: new Map(), inflight: new Map(), queue: [], active: 0, clock: 0 };
      }
      return Catalog._prefetchStore;
    }

    static prefetchKey(catalog, url) {
      const context = Catalog.themeContext();
      const handle = Catalog.collectionHandle(url);
      return JSON.stringify({
        url: url.href,
        locale: Catalog.localePrefix(url.pathname),
        view: Catalog.viewForURL(url, catalog),
        profile: Catalog.routeProfile(handle),
        themeId: context.id || '',
        role: context.role || '',
        asset: (catalog.dataset.catalogAsset || '').trim(),
      });
    }

    static getPrefetchCache(key) {
      const store = Catalog.prefetchStore();
      const entry = store.cache.get(key);
      if (!entry) return null;
      if (Date.now() - entry.storedAt >= 30000) {
        store.cache.delete(key);
        return null;
      }
      entry.lastUsed = ++store.clock;
      return entry;
    }

    static removePrefetchCache(key) {
      Catalog.prefetchStore().cache.delete(key);
    }

    static storePrefetchCache(entry) {
      const store = Catalog.prefetchStore();
      entry.storedAt = Date.now();
      entry.lastUsed = ++store.clock;
      store.cache.set(entry.key, entry);
      while (store.cache.size > 6) {
        let oldestKey = null;
        let oldestUse = Infinity;
        for (const [key, value] of store.cache) {
          if (value.lastUsed < oldestUse) { oldestUse = value.lastUsed; oldestKey = key; }
        }
        if (oldestKey == null) break;
        store.cache.delete(oldestKey);
      }
      return entry;
    }

    static enqueuePrefetch(catalog, targetURL, options = {}) {
      if (!Catalog.prefetchAllowed()) return Promise.resolve(null);
      const key = Catalog.prefetchKey(catalog, targetURL);
      const cached = Catalog.getPrefetchCache(key);
      if (cached) return Promise.resolve(cached);
      const store = Catalog.prefetchStore();
      const existing = store.inflight.get(key);
      if (existing) return existing;
      let job;
      const promise = new Promise(resolve => {
        job = { catalog, targetURL: new URL(targetURL.href), options, key, resolve, promise: null, started: false };
      });
      job.promise = promise;
      store.inflight.set(key, promise);
      store.queue.push(job);
      while (store.queue.length > Catalog.prefetchQueueLimit()) {
        const dropped = store.queue.shift();
        if (!dropped || dropped.started) continue;
        dropped.cancelled = true;
        if (store.inflight.get(dropped.key) === dropped.promise) store.inflight.delete(dropped.key);
        dropped.resolve(null);
      }
      Catalog.pumpPrefetch();
      return promise;
    }

    static pumpPrefetch() {
      const store = Catalog.prefetchStore();
      while (store.active < 2 && store.queue.length) {
        const job = store.queue.shift();
        const cached = Catalog.getPrefetchCache(job.key);
        if (cached) {
          job.resolve(cached);
          if (store.inflight.get(job.key) === job.promise) store.inflight.delete(job.key);
          continue;
        }
        store.active += 1;
        job.started = true;
        Catalog.runPrefetch(job).then(job.resolve, () => job.resolve(null)).finally(() => {
          store.active -= 1;
          if (store.inflight.get(job.key) === job.promise) store.inflight.delete(job.key);
          Catalog.pumpPrefetch();
        });
      }
    }

    static async runPrefetch(job) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort('timeout'), Catalog.requestTimeout());
      try {
        if (!Catalog.prefetchAllowed()) return null;
        if (Catalog.prefetchKey(job.catalog, job.targetURL) !== job.key) return null;
        const result = await job.catalog.fetchCatalogMarkup(job.targetURL, job.options, {
          signal: controller.signal,
          isActive: () => !controller.signal.aborted,
          deferValidation: true,
        });
        if (controller.signal.aborted || Catalog.prefetchKey(job.catalog, job.targetURL) !== job.key) return null;
        return Catalog.storePrefetchCache({
          key: job.key,
          markup: result.markup,
          responseIsFull: result.responseIsFull,
        });
      } catch (_) {
        return null;
      } finally {
        clearTimeout(timeout);
      }
    }

    static cancelQueuedPrefetch(catalog) {
      const store = Catalog.prefetchStore();
      const retained = [];
      store.queue.forEach(job => {
        if (job.catalog !== catalog || job.started) {
          retained.push(job);
          return;
        }
        job.cancelled = true;
        if (store.inflight.get(job.key) === job.promise) store.inflight.delete(job.key);
        job.resolve(null);
      });
      store.queue = retained;
    }

    static waitForPrefetch(promise, signal) {
      return new Promise((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          signal?.removeEventListener?.('abort', onAbort);
        };
        const onAbort = () => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(signal.reason || new Error('Prefetch wait aborted'));
        };
        if (signal?.aborted) return onAbort();
        signal?.addEventListener?.('abort', onAbort, { once: true });
        promise.then(value => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(value);
        }, error => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        });
      });
    }

    static isSameRoute(catalog, url) {
      const currentLocation = new URL(location.href);
      const currentPath = catalog.dataset.catalogRoutePath || currentLocation.pathname;
      const currentHandle = catalog.dataset.catalogRouteHandle || Catalog.collectionHandle(currentLocation);
      const currentPage = catalog.dataset.catalogCurrentPage || Catalog.pageForURL(currentLocation);
      return currentHandle === Catalog.collectionHandle(url) &&
        Catalog.localePrefix(currentPath) === Catalog.localePrefix(url.pathname) &&
        catalog.dataset.view === Catalog.viewForURL(url, catalog) &&
        String(currentPage) === Catalog.pageForURL(url);
    }

    static isPlainNavigation(event, link) {
      const target = (link?.getAttribute?.('target') || '').toLowerCase();
      return (!event.button || event.button === 0) && !event.metaKey && !event.ctrlKey &&
        !event.shiftKey && !event.altKey && !link?.hasAttribute?.('download') &&
        (!target || target === '_self');
    }

    previewThemeId(current = new URL(location.href)) {
      const runtimeTheme = Catalog.themeContext();
      const documentTheme = runtimeTheme.id;
      const role = runtimeTheme.role;
      const requested = current.searchParams.get('preview_theme_id') || '';
      if (!documentTheme && !role) return requested;
      if (role === 'unpublished' && documentTheme) {
        if (requested && requested !== documentTheme) return null;
        return documentTheme;
      }
      return '';
    }

    prepareNavigationURL(href, current = new URL(location.href), { catalogView = true } = {}) {
      let target;
      try { target = new URL(href, current.href); } catch (_) { return null; }
      if (target.origin !== current.origin) return null;
      Catalog.normalizeSingletonParams(target);
      const preview = this.previewThemeId(current);
      if (preview === null) return null;
      if (preview && !Catalog.hasRepeatedFilterValues(target)) target.searchParams.set('preview_theme_id', preview);
      else target.searchParams.delete('preview_theme_id');
      for (const key of ['_fd', 'pb', 'campaign', 'locale', 'market', 'country', 'language']) {
        if (current.searchParams.has(key) && !target.searchParams.has(key)) target.searchParams.set(key, current.searchParams.get(key));
      }
      for (const [key, value] of current.searchParams) {
        if (key.startsWith('utm_') && !target.searchParams.has(key)) target.searchParams.set(key, value);
      }
      target.searchParams.delete('section_id');
      const targetHandle = Catalog.collectionHandle(target);
      if (catalogView && Catalog.routeProfile(targetHandle) && !target.searchParams.has('view')) {
        target.searchParams.set('view', this.dataset.view || 'catalog');
      }
      return Catalog.normalizeSingletonParams(target);
    }

    static parseThemeAssignment(text) {
      const marker = /(?:^|[;\n])\s*(?:window\.)?Shopify\.theme\s*=\s*/g.exec(text || '');
      if (!marker) return null;
      const start = marker.index + marker[0].length;
      if (text[start] !== '{') return null;
      let depth = 0;
      let quote = '';
      let escaped = false;
      for (let index = start; index < text.length; index += 1) {
        const character = text[index];
        if (quote) {
          if (escaped) escaped = false;
          else if (character === '\\') escaped = true;
          else if (character === quote) quote = '';
          continue;
        }
        if (character === '"' || character === "'") { quote = character; continue; }
        if (character === '{') depth += 1;
        if (character === '}') {
          depth -= 1;
          if (depth === 0) {
            try {
              const value = JSON.parse(text.slice(start, index + 1));
              return value && typeof value === 'object' ? value : null;
            } catch (_) { return null; }
          }
        }
      }
      return null;
    }

    static themeContext(targetDoc) {
      const runtime = !targetDoc && typeof window !== 'undefined' ? window.Shopify?.theme : null;
      let id = (runtime?.id || '').toString().trim();
      let role = (runtime?.role || '').toString().trim();
      const sourceDocument = targetDoc || (typeof document !== 'undefined' ? document : null);
      const scripts = sourceDocument?.querySelectorAll?.('script') || [];
      for (const script of scripts) {
        const value = Catalog.parseThemeAssignment(script.textContent || '');
        if (!value) continue;
        if (value.id != null) id = String(value.id).trim();
        if (value.role != null) role = String(value.role).trim();
        break;
      }
      return { id, role };
    }

    static themeContextFromMarkup(markup) {
      const marker = /(?:^|[;\n>])\s*(?:window\.)?Shopify\.theme\s*=\s*/g.exec(markup || '');
      if (!marker) return { id: '', role: '' };
      const value = Catalog.parseThemeAssignment(`Shopify.theme = ${(markup || '').slice(marker.index + marker[0].length)}`);
      return {
        id: value?.id == null ? '' : String(value.id).trim(),
        role: value?.role == null ? '' : String(value.role).trim(),
      };
    }

    static jsonLdInfo(script) {
      let value;
      try { value = JSON.parse(script.textContent || ''); } catch (_) { return { identity: '', types: [] }; }
      const nodes = [];
      const visit = node => {
        if (Array.isArray(node)) { node.forEach(visit); return; }
        if (!node || typeof node !== 'object') return;
        if (node['@type'] != null || node['@id'] != null) nodes.push(node);
        if (node['@graph']) visit(node['@graph']);
      };
      visit(value);
      const typeName = type => String(type).split(/[\/#]/).pop();
      const types = [...new Set(nodes.flatMap(node => {
        const values = Array.isArray(node['@type']) ? node['@type'] : [node['@type']];
        return values.filter(Boolean).map(typeName);
      }))].sort();
      const ids = [...new Set(nodes.map(node => node['@id']).filter(Boolean).map(String))].sort();
      const typeIdentity = types.length ? `type:${types.join('|')}` : '';
      return {
        identity: ids.length ? `id:${ids.join('|')}` : typeIdentity,
        typeIdentity,
        types,
      };
    }

    static isSitewideJSONLD(script) {
      const types = Catalog.jsonLdInfo(script).types;
      const sitewide = new Set(['Organization', 'OnlineBusiness', 'WebSite']);
      return types.length > 0 && types.every(type => sitewide.has(type));
    }

    static validateTargetRoot(catalog, root, url, options = {}) {
      if (!root?.dataset) return !options.full && !catalog.dataset.catalogAsset;
      if (!root?.dataset || root.dataset.catalogSupported !== 'true') return false;
      const handle = Catalog.collectionHandle(url);
      const profile = Catalog.routeProfile(handle);
      if (!handle || !profile || root.dataset.catalogRouteHandle !== handle || root.dataset.catalogProfile !== profile) return false;
      if (options.routeHandle && options.routeHandle !== handle) return false;
      if (options.profile && options.profile !== profile) return false;
      const requestedView = Catalog.viewForURL(url, catalog);
      if (root.dataset.view && root.dataset.view !== requestedView) return false;
      if (root.dataset.catalogCurrentPage && root.dataset.catalogCurrentPage !== Catalog.pageForURL(url)) return false;
      if (root.dataset.catalogRoutePath && Catalog.localePrefix(root.dataset.catalogRoutePath) !== Catalog.localePrefix(url.pathname)) return false;
      const currentAsset = (catalog.dataset.catalogAsset || '').trim();
      const targetAsset = (root.dataset.catalogAsset || '').trim();
      if (currentAsset && (!targetAsset || targetAsset !== currentAsset)) return false;
      if (!options.full && catalog.dataset.section && root.dataset.section && root.dataset.section !== catalog.dataset.section) return false;
      const currentContext = Catalog.themeContext();
      if (options.full) {
        const targetContext = Catalog.themeContext(options.document);
        if (currentContext.id && targetContext.id !== currentContext.id) return false;
        if (currentContext.role && targetContext.role !== currentContext.role) return false;
        if (currentContext.id && !targetContext.id) return false;
      }
      const requestedPreview = options.requestURL?.searchParams.get('preview_theme_id') || '';
      if (options.requirePreviewId && currentContext.role === 'unpublished' && currentContext.id && requestedPreview !== currentContext.id) return false;
      if (!options.full && options.previewContext) {
        if (currentContext.id && options.previewContext.id !== currentContext.id) return false;
        if (currentContext.role && options.previewContext.role !== currentContext.role) return false;
      }
      if (!options.full && currentContext.role === 'unpublished' && currentContext.id && requestedPreview !== currentContext.id && !options.previewContext) return false;
      if (!options.full && currentContext.role === 'main' && requestedPreview) return false;
      return true;
    }

    static isFullDocument(markup) {
      return /<html(?:\s|>)/i.test(markup) && /<body(?:\s|>)/i.test(markup);
    }

    static disposeCatalog(catalog) {
      catalog.querySelectorAll?.('vx-product-gallery').forEach(gallery => gallery.disconnectedCallback?.());
      catalog.events?.abort?.();
      catalog.request?.abort?.();
    }

    static syncDocumentMetadata(targetDoc) {
      if (typeof document === 'undefined' || !targetDoc) return;
      if (typeof targetDoc.title === 'string' && targetDoc.title) document.title = targetDoc.title;
      const head = document.head;
      const targetHead = targetDoc.head || targetDoc.querySelector?.('head');
      if (!head || !targetHead) return;
      const targetMeta = [...(targetHead.querySelectorAll?.('meta[name], meta[property]') || [])].filter(meta => {
        const key = meta.getAttribute?.('name') || meta.getAttribute?.('property') || '';
        return key === 'description' || key.startsWith('og:') || key.startsWith('twitter:');
      });
      const targetMetaKeys = new Set(targetMeta.map(meta => meta.getAttribute('name') || meta.getAttribute('property')));
      [...(head.querySelectorAll?.('meta[name], meta[property]') || [])].forEach(meta => {
        const key = meta.getAttribute?.('name') || meta.getAttribute?.('property') || '';
        if ((key === 'description' || key.startsWith('og:') || key.startsWith('twitter:')) && !targetMetaKeys.has(key)) meta.remove?.();
      });
      targetMeta.forEach(meta => {
        const key = meta.getAttribute('name') || meta.getAttribute('property');
        const attr = meta.hasAttribute('name') ? 'name' : 'property';
        let current = head.querySelector?.(`meta[${attr}="${key}"]`);
        if (!current) {
          current = document.createElement?.('meta');
          current?.setAttribute(attr, key);
          if (current) head.append(current);
        }
        current?.setAttribute('content', meta.getAttribute('content') || '');
      });
      const targetCanonical = targetHead.querySelector?.('link[rel="canonical"]');
      if (targetCanonical) {
        let canonical = head.querySelector?.('link[rel="canonical"]');
        if (!canonical) {
          canonical = document.createElement?.('link');
          canonical?.setAttribute('rel', 'canonical');
          if (canonical) head.append(canonical);
        }
        canonical?.setAttribute('href', targetCanonical.getAttribute('href') || '');
      }
      const targetJSON = [...(targetHead.querySelectorAll?.('script[type="application/ld+json"]') || [])];
      const currentJSON = [...(head.querySelectorAll?.('script[type="application/ld+json"]') || [])];
      const targetByIdentity = new Map();
      const targetByType = new Map();
      targetJSON.forEach(source => {
        const info = Catalog.jsonLdInfo(source);
        const identity = info.identity;
        if (identity && !targetByIdentity.has(identity)) targetByIdentity.set(identity, source);
        if (info.typeIdentity && !targetByType.has(info.typeIdentity)) targetByType.set(info.typeIdentity, source);
      });
      const consumed = new Set();
      currentJSON.forEach(script => {
        const info = Catalog.jsonLdInfo(script);
        const source = [targetByIdentity.get(info.identity), targetByType.get(info.typeIdentity)]
          .find(candidate => candidate && !consumed.has(candidate));
        if (source) {
          script.textContent = source.textContent || '';
          consumed.add(source);
        } else if (!Catalog.isSitewideJSONLD(script)) {
          script.remove?.();
        }
      });
      targetJSON.forEach(source => {
        if (consumed.has(source)) return;
        const script = document.createElement?.('script');
        if (!script) return;
        script.type = 'application/ld+json';
        script.textContent = source.textContent || '';
        head.append(script);
      });
    }

    static navigateFully(url) {
      if (typeof window !== 'undefined' && window.location?.assign) window.location.assign(url.href);
      else if (typeof location !== 'undefined' && location.assign) location.assign(url.href);
    }

    positionEditorials() {
      const cards = [...this.querySelectorAll('.vxc-editorial')];
      let anchor = this.querySelectorAll('.vxc-product')[this.gridViewport.matches ? 7 : 5];
      if (!anchor || !cards.length || anchor.nextElementSibling === cards[0]) return;
      cards.forEach(card => { anchor.insertAdjacentElement('afterend', card); anchor = card; });
    }

    showGroups(key) {
      this.querySelectorAll('[data-filter-group]').forEach(group => { group.hidden = key !== 'all' && group.dataset.filterGroup !== key; });
      const selected = [...this.querySelectorAll('[data-filter-group]')].find(group => group.dataset.filterGroup === key);
      const title = this.querySelector('.vxc-panel-heading h2');
      this.allTitle ||= title.textContent;
      title.textContent = selected ? selected.querySelector('legend').textContent : this.allTitle;
      this.querySelector('[data-all-groups]').hidden = key === 'all';
    }

    openFilters(opener) {
      this.opener = opener;
      this.lastFilter = opener.dataset.openFilter;
      this.showGroups(this.lastFilter);
      this.dialog.showModal();
      this.dialog.querySelector('[data-close-filter]').focus();
    }

    handleColorActivation(event, colorOption) {
      if (!Catalog.canSelectColor(colorOption)) {
        const isCurrentColor = Catalog.isCurrentColor(colorOption);
        Catalog.invalidateCurrentColorRequest(colorOption);
        if (isCurrentColor) event.preventDefault();
        return false;
      }
      event.preventDefault();
      this.selectColor(colorOption, { fallback: () => Catalog.followNativeColorLink(colorOption) });
      return true;
    }

    static isPlainActivation(event) {
      return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    static isPlainKeyboardActivation(event) {
      return ['Enter', ' '].includes(event.key) && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    static canSelectColor(option) {
      const card = option?.closest?.('[data-vxc-card]');
      if (!card || !card.isConnected || !option.dataset.vxcVariantUrl || option.dataset.vxcVariantId === card.dataset.variantId) return false;
      const galleryTemplate = Catalog.findColorGallery(card, option);
      const frontSource = option.dataset['vxcMedia-1000'] || option.dataset['vxcMedia-800'] || option.dataset['vxcMedia-320'];
      return Boolean(galleryTemplate?.content?.firstElementChild && frontSource);
    }

    static isCurrentColor(option) {
      const card = option?.closest?.('[data-vxc-card]');
      return Boolean(card?.isConnected && option?.dataset?.vxcVariantId && option.dataset.vxcVariantId === card.dataset.variantId);
    }

    static invalidateCurrentColorRequest(option) {
      const card = option?.closest?.('[data-vxc-card]');
      if (!card || !card.isConnected || option.dataset.vxcVariantId !== card.dataset.variantId) return false;
      card.__vxcColorRequest = (card.__vxcColorRequest || 0) + 1;
      return true;
    }

    static followNativeColorLink(option) {
      const link = option?.querySelector?.('a');
      if (link?.href && typeof window !== 'undefined' && window.location) window.location.href = link.href;
    }

    async selectColor(option, { fallback } = {}) {
      const card = option.closest('[data-vxc-card]');
      if (!card || !card.isConnected) return false;
      const requestId = (card.__vxcColorRequest || 0) + 1;
      card.__vxcColorRequest = requestId;
      if (option.dataset.vxcVariantId && option.dataset.vxcVariantId === card.dataset.variantId) {
        return false;
      }

      const galleryTemplate = Catalog.findColorGallery(card, option);
      const frontSource = option.dataset['vxcMedia-1000'] || option.dataset['vxcMedia-800'] || option.dataset['vxcMedia-320'];
      if (!galleryTemplate || !frontSource) {
        fallback?.();
        return false;
      }
      if (!(await Catalog.mediaReady(frontSource))) {
        if (card.isConnected && card.__vxcColorRequest === requestId) fallback?.();
        return false;
      }
      if (!card.isConnected || option.isConnected === false || card.__vxcColorRequest !== requestId) return false;

      const nextGallery = galleryTemplate.content?.firstElementChild?.cloneNode(true);
      const currentGallery = card.querySelector('vx-product-gallery');
      if (!nextGallery || !currentGallery || !currentGallery.isConnected || card.__vxcColorRequest !== requestId) {
        fallback?.();
        return false;
      }

      const wasPaused = Boolean(currentGallery.paused);
      const wasExplicit = Boolean(currentGallery.explicit);
      currentGallery.clearTimer?.();
      const initialPending = currentGallery.initialAdvancePending;
      const initialRemaining = currentGallery.initialRemaining;
      currentGallery.replaceWith(nextGallery);
      if (!card.isConnected || card.__vxcColorRequest !== requestId) return false;
      nextGallery.paused = wasPaused;
      nextGallery.explicit = wasExplicit;
      if (typeof initialPending === 'boolean') {
        nextGallery.initialAdvancePending = initialPending;
        nextGallery.initialRemaining = initialRemaining;
      }
      nextGallery.sync?.();
      Catalog.updateCardState(card, option.dataset);
      return true;
    }

    static findColorGallery(card, option) {
      return [...card.querySelectorAll('template[data-vxc-color-gallery]')].find(template =>
        template.dataset.vxcVariantId === option.dataset.vxcVariantId &&
        template.dataset.vxcColor === option.dataset.vxcColor
      );
    }

    static mediaReady(src) {
      if (!src || typeof Image === 'undefined') return Promise.resolve(true);
      return new Promise(resolve => {
        const probe = new Image();
        probe.onload = async () => {
          try { await probe.decode?.(); resolve(true); } catch (_) { resolve(false); }
        };
        probe.onerror = () => resolve(false);
        probe.src = src;
      });
    }

    static updateCardState(card, data) {
      if (!card || !data) return;
      const variantUrl = data.vxcVariantUrl || '';
      const color = data.vxcColor || '';
      card.dataset.variantId = data.vxcVariantId || '';
      card.dataset.selectedColor = color;
      card.querySelectorAll('[data-vxc-product-link]').forEach(link => {
        if (variantUrl) link.href = variantUrl;
        const productName = card.dataset.vxcProductName || link.dataset.vxcProductName || link.textContent?.trim?.();
        if (productName && color) link.setAttribute('aria-label', `${productName} · ${color}`);
      });
      const price = card.querySelector('[data-vxc-price-current]');
      if (price && data.vxcPriceFormatted) price.textContent = data.vxcPriceFormatted;
      const compare = card.querySelector('[data-vxc-price-compare]');
      if (compare) {
        compare.textContent = data.vxcCompareFormatted || '';
        compare.hidden = !data.vxcCompareFormatted;
      }
      card.querySelector('[data-vxc-price]')?.classList.toggle('vxc-price--sale', Boolean(data.vxcCompareFormatted));
      const note = card.querySelector('[data-vxc-price-note]');
      if (note) {
        note.textContent = data.vxcPriceNote || '';
        note.hidden = !data.vxcPriceNote;
      }
      const colorName = card.querySelector('[data-vxc-color-name]');
      if (colorName) colorName.textContent = color;
      const availability = card.querySelector('[data-vxc-availability]');
      if (availability) {
        const available = data.vxcAvailable !== 'false';
        availability.hidden = available;
        availability.textContent = data.vxcAvailability || availability.textContent;
      }
      card.querySelectorAll('[data-vxc-color-option]').forEach(option => {
        const selected = option.dataset.vxcVariantId === data.vxcVariantId || option.dataset.vxcColor === color;
        option.toggleAttribute('data-vxc-selected', selected);
        const link = option.querySelector('a');
        if (selected) link?.setAttribute('aria-current', 'true');
        else link?.removeAttribute('aria-current');
      });
    }

    formURL() {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || ['page', 'sort_by', 'section_id'].includes(key)) url.searchParams.delete(key);
      }
      const data = new FormData(this.form);
      for (const [key, value] of data) {
        if (value !== '') url.searchParams.append(key, value);
      }
      url.searchParams.set('view', this.dataset.view);
      Catalog.normalizeSingletonParams(url);
      url.hash = '';
      return url;
    }

    async fetchCatalogMarkup(targetURL, options = {}, transport = {}) {
      const signal = transport.signal;
      const isActive = transport.isActive || (() => true);
      const active = () => !signal?.aborted && isActive();
      const sameRoute = Catalog.isSameRoute(this, targetURL);
      const currentContext = Catalog.themeContext();
      const previewMultiFilter = currentContext.role === 'unpublished' && Boolean(currentContext.id) && Catalog.hasRepeatedFilterValues(targetURL);
      const commitURL = new URL(targetURL.href);
      let bootstrapURL = null;
      if (previewMultiFilter) {
        const previewId = this.previewThemeId(new URL(location.href));
        if (!previewId) throw new Error('Preview context unavailable');
        commitURL.searchParams.delete('preview_theme_id');
        bootstrapURL = Catalog.collapseFilterValues(commitURL);
        bootstrapURL.searchParams.set('preview_theme_id', previewId);
      }
      const fetchURL = new URL(commitURL.href);
      if (sameRoute) fetchURL.searchParams.set('section_id', this.dataset.section);
      else fetchURL.searchParams.delete('section_id');
      const readMarkup = async requestURL => {
        const response = await fetch(requestURL, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Response unavailable');
        return response.text();
      };
      let verifiedPreviewContext = null;
      if (bootstrapURL) {
        const bootstrapMarkup = await readMarkup(bootstrapURL);
        if (!active() || !Catalog.isFullDocument(bootstrapMarkup)) throw new Error('Invalid preview bootstrap');
        if (transport.deferValidation) {
          const bootstrapContext = Catalog.themeContextFromMarkup(bootstrapMarkup);
          const currentContext = Catalog.themeContext();
          if (!bootstrapContext.id || !bootstrapContext.role ||
            (currentContext.id && bootstrapContext.id !== currentContext.id) ||
            (currentContext.role && bootstrapContext.role !== currentContext.role)) throw new Error('Invalid preview bootstrap');
          verifiedPreviewContext = bootstrapContext;
        } else {
          const bootstrapDoc = new DOMParser().parseFromString(bootstrapMarkup, 'text/html');
          const bootstrapRoot = bootstrapDoc.querySelector('vx-catalog');
          if (!bootstrapRoot || !Catalog.validateTargetRoot(this, bootstrapRoot, commitURL, {
            full: true,
            routeHandle: options.routeHandle,
            profile: options.profile,
            document: bootstrapDoc,
            requestURL: bootstrapURL,
            requirePreviewId: true,
          })) throw new Error('Invalid preview bootstrap');
          verifiedPreviewContext = Catalog.themeContext(bootstrapDoc);
        }
        if (!active()) throw new Error('Stale preview bootstrap');
      }
      const markup = await readMarkup(fetchURL);
      const responseIsFull = Catalog.isFullDocument(markup);
      if (!sameRoute && !responseIsFull) throw new Error('Full document required');
      if (!active()) throw new Error('Stale response');
      if (transport.deferValidation) return { markup, responseIsFull, sameRoute, commitURL, fetchURL };
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const replacement = doc.querySelector('vx-catalog');
      if (!replacement || !active() || !Catalog.validateTargetRoot(this, replacement, commitURL, {
        full: responseIsFull,
        routeHandle: options.routeHandle,
        profile: options.profile,
        document: doc,
        requestURL: fetchURL,
        previewContext: verifiedPreviewContext,
      })) throw new Error('Invalid response');
      return { markup, responseIsFull, doc, replacement, sameRoute, commitURL, fetchURL };
    }

    validatePrefetchEntry(targetURL, options, entry) {
      if (!entry?.markup) return null;
      const sameRoute = Catalog.isSameRoute(this, targetURL);
      const responseIsFull = Catalog.isFullDocument(entry.markup);
      if (!sameRoute && !responseIsFull) return null;
      let doc;
      try { doc = new DOMParser().parseFromString(entry.markup, 'text/html'); } catch (_) { return null; }
      const replacement = doc?.querySelector?.('vx-catalog');
      const currentContext = Catalog.themeContext();
      const previewContext = currentContext.role === 'unpublished' && currentContext.id && Catalog.hasRepeatedFilterValues(targetURL) ? currentContext : null;
      if (!replacement || !Catalog.validateTargetRoot(this, replacement, targetURL, {
        full: responseIsFull,
        routeHandle: options.routeHandle,
        profile: options.profile,
        document: doc,
        requestURL: targetURL,
        previewContext,
      })) return null;
      return { markup: entry.markup, responseIsFull, doc, replacement, sameRoute, commitURL: new URL(targetURL.href) };
    }

    async load(url, options = {}) {
      const targetURL = this.prepareNavigationURL(url);
      if (!targetURL) return false;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const requestId = (this.requestSequence || 0) + 1;
      this.requestSequence = requestId;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-status]')?.replaceChildren?.(this.dataset.loading);
      const errorBox = this.querySelector('.vxc-error');
      if (errorBox) errorBox.hidden = true;
      const submit = this.form?.querySelector('[type=submit]');
      if (submit) submit.disabled = true;
      const timeout = setTimeout(() => request.abort('timeout'), Catalog.requestTimeout());
      try {
        const activeRequest = () => !request.signal.aborted && request === this.request && requestId === this.requestSequence && this.isConnected;
        const cacheKey = Catalog.prefetchKey(this, targetURL);
        let transportResult = null;
        const cached = Catalog.getPrefetchCache(cacheKey);
        if (cached) transportResult = this.validatePrefetchEntry(targetURL, options, cached);
        if (cached && !transportResult) Catalog.removePrefetchCache(cacheKey);
        if (!transportResult) {
          const sharedPrefetch = Catalog.prefetchStore().inflight.get(cacheKey);
          if (sharedPrefetch) {
            await Catalog.waitForPrefetch(sharedPrefetch, request.signal);
            if (!activeRequest()) throw new Error('Stale prefetch response');
            const warmed = Catalog.getPrefetchCache(cacheKey);
            if (warmed) transportResult = this.validatePrefetchEntry(targetURL, options, warmed);
            if (warmed && !transportResult) Catalog.removePrefetchCache(cacheKey);
          }
        }
        if (!transportResult) {
          transportResult = await this.fetchCatalogMarkup(targetURL, options, {
            signal: request.signal,
            isActive: activeRequest,
          });
        }
        if (!activeRequest()) throw new Error('Stale response');
        const { doc, replacement, responseIsFull, sameRoute, commitURL } = transportResult;
        if (responseIsFull) Catalog.syncDocumentMetadata(doc);
        if (!activeRequest()) throw new Error('Stale response');
        if (options.history !== false) {
          if (typeof history.replaceState === 'function') {
            history.replaceState({ ...(history.state || {}), vxcCatalogScrollY: typeof window !== 'undefined' ? window.scrollY || 0 : 0 }, '', location.href);
          }
          history.pushState({ vxcCatalogScrollY: 0 }, '', commitURL.href);
        }
        Catalog.disposeCatalog(this);
        this.dialog.close();
        this.replaceWith(replacement);
        const target = options.focus === 'sort' ? replacement.querySelector('[data-sort]') :
          options.focus === 'count' ? replacement.querySelector('[data-result-count]') :
            [...replacement.querySelectorAll('[data-open-filter]')].find(el => el.dataset.openFilter === options.focus);
        target?.focus({ preventScroll: true });
        const status = replacement.querySelector('[data-status]');
        const count = replacement.querySelector('[data-result-count]');
        if (status && count) status.textContent = count.textContent;
        if (options.restoreScroll != null && typeof window !== 'undefined' && typeof window.scrollTo === 'function') window.scrollTo({ top: options.restoreScroll, left: 0, behavior: 'auto' });
        else if (options.scroll) replacement.querySelector('.vxc-querybar')?.scrollIntoView?.({ block: 'start' });
        else if ((options.scrollTop || !sameRoute) && typeof window !== 'undefined' && typeof window.scrollTo === 'function') window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return true;
      } catch (_) {
        if (request !== this.request || !this.isConnected) return;
        if (request.signal.aborted && request.signal.reason !== 'timeout') return;
        if (options.history === false) {
          Catalog.navigateFully(targetURL);
          return false;
        }
        this.dialog.close();
        const error = this.querySelector('.vxc-error');
        if (error) {
          error.querySelector('p').textContent = this.dataset.error;
          const retry = error.querySelector('[data-retry]') || error.querySelector('a');
          if (retry) retry.href = targetURL.href;
          error.hidden = false;
        }
        const status = this.querySelector('[data-status]');
        if (status) status.textContent = '';
        return false;
      } finally {
        clearTimeout(timeout);
        if (this.isConnected && request === this.request && requestId === this.requestSequence) {
          this.removeAttribute('aria-busy');
          const currentSubmit = this.form?.querySelector('[type=submit]');
          if (currentSubmit) currentSubmit.disabled = false;
        }
      }
    }

    disconnectedCallback() {
      this.request?.abort();
      this.events?.abort();
      Catalog.cancelQueuedPrefetch(this);
      this.prefetchTimers?.forEach(timer => clearTimeout(timer));
      this.prefetchTimers?.clear();
      this.ready = false;
    }
  }
  customElements.define('vx-product-gallery', ProductGallery);
  customElements.define('vx-catalog', Catalog);
})();
